import { usePDF } from '@tato30/vue-pdf'
import type { PDFOptions, PDFSrc } from '@tato30/vue-pdf'
import type { Ref } from 'vue'
import { computed, unref } from 'vue'
import { CMAP_URL, CMAP_PACKED } from '@/utils/pdfjs'

/**
 * Wrapper around usePDF that automatically adds cMap configuration.
 * This prevents "Error during font loading: Ensure that the cMapUrl and cMapPacked API parameters are provided" warnings.
 *
 * Usage: Replace `usePDF(url)` with `usePDFWithCmaps(url)`
 */
export function usePDFWithCmaps(
  src: PDFSrc | Ref<PDFSrc>,
  options?: PDFOptions
): ReturnType<typeof usePDF> {
  const configuredSrc = computed(() => {
    const srcValue = unref(src)

    if (!srcValue) return srcValue

    // If it's a string, wrap it with cMap config
    if (typeof srcValue === 'string') {
      return {
        url: srcValue,
        cMapUrl: CMAP_URL,
        cMapPacked: CMAP_PACKED
      }
    }

    // If it's already an object, add cMap config
    if (typeof srcValue === 'object' && !ArrayBuffer.isView(srcValue)) {
      return {
        cMapUrl: CMAP_URL,
        cMapPacked: CMAP_PACKED,
        ...srcValue
      }
    }

    return srcValue
  })

  return usePDF(configuredSrc as any, options)
}
